import { css } from 'twin.macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import { BsArrowRepeat as RefreshIcon } from 'react-icons/bs';
import dayjs from 'dayjs';
import OurAccordion from '@/_elements/OurAccordion';
import Avatar from '@/components/Admin/reusableComponents/Avatar';
import MessageTime from '../Common/MessageTime';
import { userformdataDiscussionApi } from '@/_services/api/userformdata/userformdataDiscussion';
import AutoExpandingTextarea from '@/_elements/Text/AutoExpandingTextarea';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const messagesWrapper = css`
    height: fit-content;
    overflow-y: auto;
    border: 1px solid #ececec;
    // border-radius: 8px;
    padding: 0.75rem;
    background: #fafafa;
    margin-right: 64px;
`;

const messageRow = css`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 1.5rem;
    margin-top: 3rem;
`;

const messageContent = css`
    max-width: 90%;
`;

const authorLabel = css`
    font-size: 1.35rem;
    font-weight: 600;
    color: #3f3f3f;
    margin-bottom: 0.25rem;
`;

const bubble = css`
    background: #ededed;
    color: #4a4a4a;
    padding: 0.65rem 0.9rem;
    border-radius: 14px;
    line-height: 1.35;
    white-space: pre-wrap;
`;

const timestamp = css`
    font-size: 1.15rem;
    color: #8a8a8a;
    margin-top: 0.25rem;
`;

const getAuthorMeta = (user = {}, currUser = {}) => {
    const currentUserId = currUser?._id || currUser?.id;
    const userId = user?._id || user;
    return {
        ...user,
        user,
        name: `${user?.name || ''}${currentUserId === userId ? ' (You)' : ''}`,
        avatar: user?.avatar || null,
    };
};

const Component = ({ currUser, userFormData }) => {
    const [discussionMessages, setDiscussionMessages] = useState([]);
    const [discussionText, setDiscussionText] = useState('');
    const [isDiscussionLoading, setIsDiscussionLoading] = useState(false);
    const [isPostingDiscussion, setIsPostingDiscussion] = useState(false);
    const [isDiscussionMuted, setIsDiscussionMuted] = useState(false);
    const [isMuteBusy, setIsMuteBusy] = useState(false);

    const userformdataId = userFormData?._id || userFormData?.id;
    const currentUserId = currUser?.id || currUser?._id;
    const submitterId = (userFormData?.user || {})?._id || (userFormData?.user || {})?.id || userFormData?.user;
    const submissionChatroomId = (userFormData?.chatroom || {})?._id || userFormData?.chatroom;
    const submissionTeamId = userFormData?.team?._id || userFormData?.team;

    const canPostDiscussion = useMemo(() => {
        if (!userformdataId || !currUser) return false;
        if (currUser?.role === 'admin') return true;
        if (submitterId && currentUserId && submitterId?.toString() === currentUserId?.toString()) return true;

        const teamRole = currUser?.teamList?.find((teamData) => {
            const teamId = teamData?.team?.id || teamData?.team?._id || teamData?.team;
            return teamId?.toString() === submissionTeamId?.toString();
        })?.role;

        if (['owner', 'teamManager'].includes(teamRole)) return true;

        const roomRole = currUser?.roomList?.find((roomData) => {
            const roomId = roomData?.room?.id || roomData?.room?._id || roomData?.room;
            return roomId?.toString() === submissionChatroomId?.toString();
        })?.role;

        return roomRole === 'supervisor';
    }, [currUser, currentUserId, submissionChatroomId, userformdataId, submissionTeamId, submitterId]);

    const loadMessages = async () => {
        if (!userformdataId) return;
        try {
            setIsDiscussionLoading(true);
            const response = await userformdataDiscussionApi.getDiscussions(userformdataId);
            setDiscussionMessages(response?.results || []);
        } catch (e) {
            if (e?.name === 'AbortError') {
                return;
            }
            toast.error(e?.message || 'Failed to load discussion');
        } finally {
            setIsDiscussionLoading(false);
        }
    };

    useEffect(() => {
        loadMessages();
    }, [userformdataId]);
    const loadMuteStatus = async () => {
        try {
            setIsMuteBusy(true);
            const response = await userformdataDiscussionApi.getNotifications(userformdataId);
            setIsDiscussionMuted(response?.mute);
        } catch (e) {
        } finally {
            setIsMuteBusy(false);
        }
    };
    useEffect(() => {
        if (!userformdataId) return;
        loadMuteStatus();
    }, [userformdataId]);

    const handleToggleMute = async () => {
        if (!userformdataId) return;
        try {
            setIsMuteBusy(true);
            const response = await userformdataDiscussionApi.updateNotifications(userformdataId, {
                mute: !isDiscussionMuted,
            });
            setIsDiscussionMuted(response?.mute);
        } catch (e) {
            toast.error(e?.message || 'Failed to update mute');
        } finally {
            setIsMuteBusy(false);
        }
    };

    const handlePostDiscussion = async () => {
        if (!discussionText.trim() || !userformdataId) return;
        try {
            setIsPostingDiscussion(true);
            const response = await userformdataDiscussionApi.createDiscussion(userformdataId, {
                text: discussionText.trim(),
            });
            setDiscussionMessages((prev) => [response, ...prev]);
            setDiscussionText('');
        } catch (e) {
            toast.error(e?.message || 'Failed to post message');
            loadMessages();
        } finally {
            setIsPostingDiscussion(false);
            loadMuteStatus();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handlePostDiscussion();
        }
    };

    const sortedMessages = useMemo(() => {
        return [...(discussionMessages || [])].sort((a, b) => {
            const aTime = new Date(a?.createdAt || 0).getTime();
            const bTime = new Date(b?.createdAt || 0).getTime();
            return aTime - bTime;
        });
    }, [discussionMessages]);

    const latestMessage = useMemo(() => {
        if (!sortedMessages.length) return null;
        return sortedMessages[sortedMessages.length - 1];
    }, [sortedMessages]);

    const lastUpdatedLabel = latestMessage?.createdAt ? dayjs(latestMessage.createdAt).format('MM-DD-YYYY h:mm A') : '';

    if (!userformdataId) return null;

    return (
        <div
            css={css`
                margin-top: 1.5rem;
                border: 1px solid #e6e6e6;
            `}
        >
            <OurAccordion
                title={
                    <div className="d-flex flex-row align-items-center justify-content-between w-100">
                        <div>
                            <b>Discussion</b>
                        </div>
                        {lastUpdatedLabel && (
                            <div
                                className="text-muted mr-2"
                                css={css`
                                    font-size: 1.25rem;
                                `}
                            >
                                Last updated at: {lastUpdatedLabel}
                            </div>
                        )}
                    </div>
                }
                titleTag="div"
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        margin-bottom: 0.75rem;
                        gap: 0.5rem;
                    `}
                >
                    <Button
                        type="button"
                        variant="outline-primary"
                        onClick={loadMessages}
                        disabled={isDiscussionLoading}
                        css={css`
                            padding: 0.2rem 0.45rem;
                        `}
                        title="Refresh discussion"
                    >
                        <RefreshIcon className={`${isDiscussionLoading ? 'spin' : ''}`} />
                    </Button>
                    {canPostDiscussion && (
                        <Button
                            type="button"
                            variant="outline-primary"
                            onClick={handleToggleMute}
                            disabled={isMuteBusy}
                            css={css`
                                padding: 0.2rem 0.45rem;
                            `}
                            title={isDiscussionMuted ? 'Muted' : 'Mute notifications'}
                        >
                            {isDiscussionMuted ? <FaBellSlash /> : <FaBell />}
                        </Button>
                    )}
                </div>
                <div css={messagesWrapper}>
                    {sortedMessages.length ? (
                        sortedMessages.map((msg) => {
                            const userData = getAuthorMeta(msg?.user || {}, currUser);
                            return (
                                <div key={msg?._id} css={[messageRow]}>
                                    <Avatar item={userData?.user || {}} />
                                    <div css={[messageContent]}>
                                        <div css={authorLabel}>{userData.name}</div>
                                        <div css={[bubble]}>{msg?.text}</div>
                                        <div css={timestamp}>
                                            <span className="px-2 text-right text-muted text-uppercase">
                                                {msg?.createdAt && <MessageTime dateAndTime={msg?.createdAt} />}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div>No messages yet.</div>
                    )}
                </div>
                {canPostDiscussion && (
                    <div
                        css={css`
                            margin-top: 0.75rem;
                            display: flex;
                            gap: 0.75rem;
                            align-items: flex-end;
                        `}
                    >
                        <AutoExpandingTextarea
                            disabled={!canPostDiscussion || isPostingDiscussion}
                            onChange={(e) => setDiscussionText(e.target.value)}
                            value={discussionText}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handlePostDiscussion}
                            disabled={!canPostDiscussion || isPostingDiscussion || !discussionText.trim()}
                        >
                            {isPostingDiscussion ? <VscLoading className="spin" /> : 'Send'}
                        </Button>
                    </div>
                )}
            </OurAccordion>
        </div>
    );
};
const UserFormDataDiscussion = (props) => (
    <ComponentErrorBoundary componentErrorMessage={'Failed to load Form Discussion div'}>
        <Component {...props} />
    </ComponentErrorBoundary>
);
const mapStateToProps = (state) => ({
    currUser: state.user.user,
});

export default connect(mapStateToProps, {})(UserFormDataDiscussion);
